import request from './request'

/**
 * 获取用户分页列表
 * @param {object} params - 查询参数
 * @param {string} [params.username] - 用户名（模糊查询）
 * @param {string} [params.phone] - 手机号（模糊查询）
 * @param {number} [params.deptId] - 部门ID
 * @param {number} [params.status] - 用户状态: 0-正常, 1-停用
 * @param {number} [params.page=1] - 当前页码
 * @param {number} [params.size=10] - 每页大小
 */
export function getUserList(params) {
  return request.get('/users/list', { params })
}

/**
 * 获取用户详情
 * @param {number} id - 用户ID
 */
export function getUser(id) {
  return request.get(`/users/${id}`)
}

/**
 * 新增用户
 * @param {object} data - 用户信息
 * @param {string} data.username - 用户名
 * @param {string} [data.displayName] - 显示名称
 * @param {string} [data.phone] - 手机号
 * @param {string} [data.password] - 密码
 * @param {number} [data.deptId] - 部门ID
 * @param {string} [data.email] - 邮箱
 * @param {number} [data.status] - 状态：0-正常，1-停用
 */
export function createUser(data) {
  return request.post('/users', data)
}

/**
 * 修改用户
 * @param {number} id - 用户ID
 * @param {object} data - 用户信息
 * @param {string} [data.username] - 用户名
 * @param {string} [data.displayName] - 显示名称
 * @param {string} [data.phone] - 手机号
 * @param {number} [data.deptId] - 部门ID
 * @param {string} [data.email] - 邮箱
 * @param {number} [data.status] - 状态：0-正常，1-停用
 * @param {number} [data.gender] - 性别：0-未知，1-男，2-女
 * @param {number[]} [data.roleIds] - 角色ID列表
 * @param {number[]} [data.postIds] - 岗位ID列表
 */
export function updateUser(id, data) {
  return request.put(`/users/${id}`, data)
}

/**
 * 获取助理绑定关系
 * @param {number} assistantUserId - 助理用户ID
 */
export function getAssistantRelations(assistantUserId) {
  return request.get('/users/assistant-relations', { params: { assistantUserId } })
}

/**
 * 保存助理绑定关系
 * @param {object} data - 绑定关系
 * @param {number} data.assistantUserId - 助理用户ID
 * @param {number} data.principalUserId - 被绑定的主用户ID
 * @param {string} data.relationType - 关系类型
 */
export function saveAssistantRelation(data) {
  return request.post('/users/assistant-relations', data)
}

/**
 * 停用助理绑定关系
 * @param {object} data - 绑定关系
 * @param {number} data.assistantUserId - 助理用户ID
 * @param {number} data.principalUserId - 被绑定的主用户ID
 * @param {string} data.relationType - 关系类型
 */
export function disableAssistantRelation(data) {
  return request.delete('/users/assistant-relations', { data })
}

/**
 * 删除用户
 * @param {number} id - 用户ID
 */
export function deleteUser(id) {
  return request.delete(`/users/${id}`)
}

/**
 * 禁用用户
 * @param {number} id - 用户ID
 */
export function disableUser(id) {
  return request.put(`/users/${id}/disable`)
}

/**
 * 获取用户的角色ID列表
 * @param {number} userId - 用户ID
 */
export function getUserRoleIds(userId) {
  return request.get(`/system/roles/user/${userId}`)
}

/**
 * 为用户分配角色
 * @param {number} userId - 用户ID
 * @param {object} data - 角色分配信息
 * @param {number[]} data.roleIds - 角色ID列表
 */
export function grantRolesToUser(userId, data) {
  return request.post(`/system/roles/user/${userId}`, data)
}

/**
 * 获取用户的岗位ID列表
 * @param {number} userId - 用户ID
 */
export function getUserPostIds(userId) {
  return request.get(`/system/user-posts/user/${userId}`)
}

/**
 * 为用户分配岗位
 * @param {number} userId - 用户ID
 * @param {object} data - 岗位分配信息
 * @param {number[]} data.postIds - 岗位ID列表
 */
export function grantPostsToUser(userId, data) {
  return request.post(`/system/user-posts/user/${userId}`, data)
}

/**
 * 查询用户数据权限
 * @param {number} userId - 用户ID
 */
export function getUserDataScope(userId) {
  return request.get(`/system/user-data-scope/user/${userId}`)
}

/**
 * 保存用户数据权限
 * @param {object} data - 数据权限信息
 * @param {number} data.userId - 用户ID
 * @param {string} data.resourceCode - 资源编码
 * @param {number} data.dataScope - 数据范围：1-全部，2-本部门，3-本部门及以下，4-仅本人，5-自定义
 * @param {number[]} [data.deptIds] - 自定义部门ID列表（当dataScope=5时使用）
 * @param {string} [data.remark] - 备注说明
 */
export function saveUserDataScope(data) {
  return request.post('/system/user-data-scope', data)
}

/**
 * 删除用户数据权限
 * @param {number} userId - 用户ID
 * @param {string} resourceCode - 资源编码
 */
export function deleteUserDataScope(userId, resourceCode) {
  return request.delete(`/system/user-data-scope/user/${userId}/resource/${resourceCode}`)
}

/**
 * 获取用户直接分配的菜单ID列表
 * @param {number} userId - 用户ID
 */
export function getUserDirectMenuIds(userId) {
  return request.get(`/system/menus/user/${userId}/direct`)
}

/**
 * 为用户直接分配菜单
 * @param {number} userId - 用户ID
 * @param {object} data - 菜单分配信息
 * @param {number} data.userId - 用户ID
 * @param {number[]} data.menuIds - 菜单ID列表
 */
export function grantUserDirectMenus(userId, data) {
  return request.post(`/system/menus/user/${userId}/direct`, data)
}

/**
 * 重置用户密码
 * @param {object} data - 重置密码信息
 * @param {number} data.userId - 用户ID
 * @param {string} data.newPassword - 重置后的新密码
 */
export function resetPassword(data) {
  return request.put('/users/resetPassword', data)
}

/**
 * 绑定账号到租户
 * @param {object} data - 绑定参数
 * @param {number} data.tenantId - 目标租户ID
 * @param {string} data.phone - 手机号（用于查找已有账号）
 * @param {number} data.deptId - 加入的部门ID
 * @param {number[]} [data.roleIds] - 分配的角色ID列表
 * @param {number[]} [data.postIds] - 分配的岗位ID列表
 * @param {string} [data.nickname] - 在此租户中的昵称
 */
export function bindTenant(data) {
  return request.post('/users/bindTenant', data)
}

/**
 * 当前登录用户修改头像
 * @param {string} avatar - OSS图片key
 */
export function updateAvatar(avatar) {
  return request.put('/users/avatar', null, { params: { avatar } })
}

/**
 * 分页查询APP用户列表
 * @param {object} params - 查询参数
 * @param {string} [params.phone] - 手机号（模糊查询）
 * @param {string} [params.nickname] - 昵称（模糊查询）
 * @param {number} [params.status] - 状态：0-正常，1-禁用
 * @param {number} [params.page] - 当前页码
 * @param {number} [params.size] - 每页大小
 */
export function getAppUserList(params) {
  return request.get('/app/admin/user/list', { params })
}
